'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';

export type CurrencyType = 'USD' | 'TWD';

interface CurrencyContextType {
  currency: CurrencyType;
  setCurrency: (currency: CurrencyType) => void;
  formatPrice: (priceUSD: number, priceTWD: number) => string;
  exchangeRate: number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

const EXCHANGE_RATE = 32; // Approximate USD to TWD rate

interface CurrencyProviderProps {
  children: ReactNode;
  defaultLanguage?: 'en' | 'zh';
}

export function CurrencyProvider({
  children,
  defaultLanguage = 'en',
}: CurrencyProviderProps) {
  const [currency, setCurrencyState] = useState<CurrencyType>(
    defaultLanguage === 'zh' ? 'TWD' : 'USD'
  );

  useEffect(() => {
    // Check localStorage first
    const savedCurrency = localStorage.getItem(
      'preferred-currency'
    ) as CurrencyType;
    if (savedCurrency && (savedCurrency === 'USD' || savedCurrency === 'TWD')) {
      setCurrencyState(savedCurrency);
    } else {
      // Default based on language
      const defaultCurrency = defaultLanguage === 'zh' ? 'TWD' : 'USD';
      setCurrencyState(defaultCurrency);
    }
  }, [defaultLanguage]);

  const setCurrency = (newCurrency: CurrencyType) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('preferred-currency', newCurrency);
  };

  const formatPrice = (priceUSD: number, priceTWD: number): string => {
    if (currency === 'TWD') {
      return `NT$${priceTWD.toLocaleString()}`;
    }
    return `$${priceUSD.toLocaleString()}`;
  };

  const value: CurrencyContextType = {
    currency,
    setCurrency,
    formatPrice,
    exchangeRate: EXCHANGE_RATE,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextType {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
